<template>
  <div v-if="visibleMeta.length" class="mt-2 flex flex-col gap-2">
    <template v-for="(item, index) in visibleMeta" :key="index">
      <div
        v-if="item.type === 'milage-expectation'"
        class="rounded-md border border-gray-100 bg-gray-50 px-3 py-2 text-xs text-gray-600"
      >
        <div>
          <span class="font-medium">{{ t('milageExpectation') }}:</span>
          {{ t(`modes.${item.mode}`) }}
          <span class="text-gray-500">
            {{ formatKm(item.milageBefore) }} -> {{ formatKm(item.milageAfter) }}
          </span>
          <span> ({{ t('delta') }} {{ formatKm(item.delta) }}) </span>
        </div>
        <div class="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-gray-500">
          <span>
            {{ t('expectedRange') }}:
            {{ formatExpectedRange(item) }}
          </span>
          <span v-if="item.includesTransfer">{{ t('includesTransfer') }}</span>
          <span v-if="item.deviationReason">
            {{ t('reason', { reason: t(`reasons.${item.deviationReason}`) }) }}
          </span>
          <span v-if="item.deviationComment">
            {{ t('comment', { comment: item.deviationComment }) }}
          </span>
        </div>
      </div>
      <AuditLogCustomerMailMeta v-else-if="item.type === 'customer-mail'" :mail="item" />
    </template>
  </div>
</template>

<script setup lang="ts">
import type {
  AuditLog,
  ChangedMilageExpectationMeta,
  SentCustomerMailMeta,
} from '@carvia/ros-client-types';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import AuditLogCustomerMailMeta from '@/components/audit-log/AuditLogCustomerMailMeta.vue';

const props = defineProps<{
  meta?: AuditLog['meta'];
}>();

const { t, locale } = useI18n();

const visibleMeta = computed(
  () =>
    props.meta?.filter(
      (item): item is ChangedMilageExpectationMeta | SentCustomerMailMeta =>
        item != null && item.type !== 'origin' && item.type !== 'actor',
    ) ?? [],
);

const formatKm = (value: number) =>
  `${new Intl.NumberFormat(locale.value).format(Math.round(value))} km`;

const formatExpectedRange = (meta: ChangedMilageExpectationMeta) => {
  if (meta.expectedMinDelta !== null && meta.expectedMaxDelta !== null) {
    return `${formatKm(meta.expectedMinDelta)} - ${formatKm(meta.expectedMaxDelta)}`;
  }
  if (meta.expectedMinDelta !== null) {
    return `>= ${formatKm(meta.expectedMinDelta)}`;
  }
  if (meta.expectedMaxDelta !== null) {
    return `<= ${formatKm(meta.expectedMaxDelta)}`;
  }
  return '-';
};
</script>

<i18n lang="json">
{
  "en": {
    "milageExpectation": "Mileage check",
    "delta": "difference",
    "expectedRange": "Expected",
    "includesTransfer": "Includes transfer",
    "reason": "Reason: {reason}",
    "comment": "Comment: {comment}",
    "modes": {
      "direct": "direct update",
      "handover": "handover",
      "return": "return"
    },
    "reasons": {
      "INSPECTION_MAINTENANCE": "Inspection / maintenance",
      "TIRE_CHANGE": "Tire change",
      "REPAIR": "Repair",
      "MARKETING": "Marketing (e.g. YouTube)",
      "PIGGYBACK_TRANSPORT": "Piggyback transport",
      "OTHER": "Other"
    }
  },
  "de": {
    "milageExpectation": "Kilometerprüfung",
    "delta": "Differenz",
    "expectedRange": "Erwartet",
    "includesTransfer": "Inklusive Transfer",
    "reason": "Grund: {reason}",
    "comment": "Kommentar: {comment}",
    "modes": {
      "direct": "direkte Änderung",
      "handover": "Übergabe",
      "return": "Rückgabe"
    },
    "reasons": {
      "INSPECTION_MAINTENANCE": "Inspektion / Wartung",
      "TIRE_CHANGE": "Reifenwechsel",
      "REPAIR": "Reparatur",
      "MARKETING": "Marketing (z. B. YouTube)",
      "PIGGYBACK_TRANSPORT": "Huckepackfahrt",
      "OTHER": "Sonstiges"
    }
  },
  "es": {
    "milageExpectation": "Comprobación de kilometraje",
    "delta": "diferencia",
    "expectedRange": "Esperado",
    "includesTransfer": "Incluye traslado",
    "reason": "Motivo: {reason}",
    "comment": "Comentario: {comment}",
    "modes": {
      "direct": "actualización directa",
      "handover": "entrega",
      "return": "devolución"
    },
    "reasons": {
      "INSPECTION_MAINTENANCE": "Inspección / mantenimiento",
      "TIRE_CHANGE": "Cambio de neumáticos",
      "REPAIR": "Reparación",
      "MARKETING": "Marketing (p. ej. YouTube)",
      "PIGGYBACK_TRANSPORT": "Transporte piggyback",
      "OTHER": "Otro"
    }
  }
}
</i18n>
